import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import type { BOMEntry } from '@shared/types';

const MATERIAL_GROUPS = [
  {
    label: '鉄鋼',
    items: ['SS400', 'S45C', 'SPCC', 'SPHC'],
  },
  {
    label: 'ステンレス',
    items: ['SUS304', 'SUS316'],
  },
  {
    label: 'アルミ',
    items: ['A5052', 'A6063'],
  },
  {
    label: '銅',
    items: ['C1100'],
  },
  {
    label: '樹脂',
    items: ['MC Nylon', 'POM', 'ABS', 'アクリル', 'ポリカーボネート', 'ゴム'],
  },
  {
    label: '木材',
    items: ['合板', 'MDF', 'ラワン', 'タモ', 'ナラ', 'ブナ'],
  },
];

const ALL_MATERIALS = MATERIAL_GROUPS.flatMap((g) => g.items);

type BOMReviewModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bom: BOMEntry[];
  onAccept: (bom: BOMEntry[]) => void;
};

export function BOMReviewModal({ open, onOpenChange, bom, onAccept }: BOMReviewModalProps) {
  const { t } = useTranslation('common');
  const [entries, setEntries] = useState<BOMEntry[]>(() => bom.map((e) => ({ ...e })));

  const updateEntry = useCallback((index: number, field: keyof BOMEntry, value: string | number) => {
    setEntries((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const addRow = useCallback(() => {
    setEntries((prev) => [
      ...prev,
      {
        number: prev.length + 1,
        name: '',
        specification: '',
        quantity: 1,
        material: '',
        moduleName: '',
      },
    ]);
  }, []);

  const removeRow = useCallback((index: number) => {
    setEntries((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((e, i) => ({ ...e, number: i + 1 }));
    });
  }, []);

  const moveRow = useCallback((from: number, to: number) => {
    if (to < 0 || to >= entries.length) return;
    setEntries((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((e, i) => ({ ...e, number: i + 1 }));
    });
  }, [entries.length]);

  const handleAccept = () => {
    onAccept(entries);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-4xl flex-col gap-0 overflow-hidden border-synergy-neutral-700 bg-synergy-bg-dark p-0">
        {/* Header */}
        <DialogHeader className="flex-shrink-0 border-b border-synergy-neutral-700/60 px-6 pb-4 pt-6">
          <div className="flex items-baseline gap-3">
            <DialogTitle className="font-display text-[15px] font-semibold tracking-tight text-synergy-text-primary">
              {t('drawing.bomTitle')}
            </DialogTitle>
            <span className="font-mono text-[11px] tabular-nums text-synergy-neutral-400">
              {entries.length} {t('drawing.bomParts')}
            </span>
          </div>
        </DialogHeader>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full border-collapse text-[12px]">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-synergy-neutral-700/60 bg-synergy-bg-secondary-dark">
                <th className="w-7 px-1 py-2.5" />
                <th className="w-9 px-1 py-2.5 text-center text-[10px] font-semibold uppercase tracking-[0.08em] text-synergy-neutral-400">
                  {t('drawing.bomNo')}
                </th>
                <th className="min-w-[100px] px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-synergy-neutral-400">
                  {t('drawing.bomName')}
                </th>
                <th className="min-w-[140px] px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-synergy-neutral-400">
                  {t('drawing.bomSpec')}
                </th>
                <th className="w-14 px-2 py-2.5 text-center text-[10px] font-semibold uppercase tracking-[0.08em] text-synergy-neutral-400">
                  {t('drawing.bomQty')}
                </th>
                <th className="min-w-[110px] px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-synergy-neutral-400">
                  {t('drawing.bomMaterial')}
                </th>
                <th className="min-w-[90px] px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-synergy-neutral-400">
                  {t('drawing.bomDimensions')}
                </th>
                <th className="min-w-[80px] px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-synergy-neutral-400">
                  {t('drawing.bomNotes')}
                </th>
                <th className="w-8 px-1 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, i) => (
                <tr
                  key={i}
                  className="group border-b border-synergy-neutral-700/30 transition-colors [@media(hover:hover)]:hover:bg-synergy-neutral-900/40"
                >
                  {/* Reorder */}
                  <td className="px-0.5 py-1">
                    <div className="flex flex-col items-center gap-0">
                      <button
                        onClick={() => moveRow(i, i - 1)}
                        disabled={i === 0}
                        className="flex h-4 w-5 items-center justify-center text-synergy-neutral-400 transition-colors disabled:opacity-0 [@media(hover:hover)]:hover:text-synergy-text-primary"
                      >
                        <ChevronUp className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => moveRow(i, i + 1)}
                        disabled={i === entries.length - 1}
                        className="flex h-4 w-5 items-center justify-center text-synergy-neutral-400 transition-colors disabled:opacity-0 [@media(hover:hover)]:hover:text-synergy-text-primary"
                      >
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </div>
                  </td>

                  {/* Number */}
                  <td className="px-1 py-1 text-center">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-synergy-neutral-800 font-mono text-[10px] font-semibold text-synergy-neutral-300">
                      {entry.number}
                    </span>
                  </td>

                  {/* Name */}
                  <td className="px-1.5 py-1">
                    <input
                      value={entry.name}
                      onChange={(e) => updateEntry(i, 'name', e.target.value)}
                      placeholder={t('drawing.partName')}
                      className="h-7 w-full rounded-md border-0 bg-transparent px-1.5 text-[12px] font-medium text-synergy-text-primary outline-none transition-colors placeholder:text-synergy-neutral-500 focus:bg-synergy-neutral-800"
                    />
                  </td>

                  {/* Specification */}
                  <td className="px-1.5 py-1">
                    <input
                      value={entry.specification}
                      onChange={(e) => updateEntry(i, 'specification', e.target.value)}
                      placeholder="—"
                      className="h-7 w-full rounded-md border-0 bg-transparent px-1.5 text-[12px] text-synergy-text-primary outline-none transition-colors placeholder:text-synergy-neutral-500 focus:bg-synergy-neutral-800"
                    />
                  </td>

                  {/* Quantity */}
                  <td className="px-1.5 py-1">
                    <input
                      type="number"
                      value={entry.quantity}
                      min={1}
                      onChange={(e) => updateEntry(i, 'quantity', parseInt(e.target.value) || 1)}
                      className="h-7 w-12 rounded-md border-0 bg-transparent px-1.5 text-center font-mono text-[12px] tabular-nums text-synergy-text-primary outline-none transition-colors focus:bg-synergy-neutral-800"
                    />
                  </td>

                  {/* Material */}
                  <td className="px-1.5 py-1">
                    <MaterialSelect
                      value={entry.material}
                      onChange={(v) => updateEntry(i, 'material', v)}
                    />
                  </td>

                  {/* Dimensions */}
                  <td className="px-1.5 py-1">
                    <input
                      value={entry.dimensions ?? ''}
                      onChange={(e) => updateEntry(i, 'dimensions', e.target.value)}
                      placeholder="—"
                      className="h-7 w-full rounded-md border-0 bg-transparent px-1.5 font-mono text-[11px] text-synergy-text-primary outline-none transition-colors placeholder:text-synergy-neutral-500 focus:bg-synergy-neutral-800"
                    />
                  </td>

                  {/* Notes */}
                  <td className="px-1.5 py-1">
                    <input
                      value={entry.notes ?? ''}
                      onChange={(e) => updateEntry(i, 'notes', e.target.value)}
                      placeholder="—"
                      className="h-7 w-full rounded-md border-0 bg-transparent px-1.5 text-[12px] text-synergy-text-secondary outline-none transition-colors placeholder:text-synergy-neutral-500 focus:bg-synergy-neutral-800"
                    />
                  </td>

                  {/* Delete */}
                  <td className="px-1 py-1">
                    <button
                      onClick={() => removeRow(i)}
                      className="flex h-6 w-6 items-center justify-center rounded-md text-brand-indigo/40 opacity-0 transition-all group-hover:opacity-100 [@media(hover:hover)]:hover:bg-brand-indigo [@media(hover:hover)]:hover:text-brand-cream"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Add row */}
          <div className="border-b border-synergy-neutral-700/30 px-3 py-2">
            <button
              onClick={addRow}
              className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-medium text-synergy-neutral-400 transition-colors [@media(hover:hover)]:hover:bg-synergy-neutral-800 [@media(hover:hover)]:hover:text-synergy-text-primary"
            >
              <Plus className="h-3 w-3" />
              {t('drawing.addRow')}
            </button>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="flex-shrink-0 border-t border-synergy-neutral-700/60 px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-9 px-4 text-xs font-medium text-synergy-text-secondary"
          >
            {t('dialog.cancel')}
          </Button>
          <Button
            onClick={handleAccept}
            className="h-9 rounded-lg bg-synergy-neutral-50 px-5 text-xs font-medium text-white transition-colors hover:bg-synergy-neutral-100"
          >
            {t('drawing.confirmAndContinue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MaterialSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('common');
  const [isCustom, setIsCustom] = useState(() => !ALL_MATERIALS.includes(value) && value !== '');

  if (isCustom) {
    return (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => { if (!value) setIsCustom(false); }}
        autoFocus
        className="h-7 w-full rounded-md border-0 bg-synergy-neutral-800 px-1.5 text-[12px] text-synergy-text-primary outline-none transition-colors focus:bg-synergy-neutral-800"
      />
    );
  }

  return (
    <select
      value={value}
      className="h-7 w-full appearance-none rounded-md border-0 bg-transparent px-1 text-[12px] text-synergy-text-primary outline-none transition-colors focus:bg-synergy-neutral-800"
      onChange={(e) => {
        if (e.target.value === '__custom__') {
          setIsCustom(true);
          onChange('');
        } else {
          onChange(e.target.value);
        }
      }}
    >
      <option value="">—</option>
      {MATERIAL_GROUPS.map((group) => (
        <optgroup key={group.label} label={group.label}>
          {group.items.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </optgroup>
      ))}
      <option value="__custom__">{t('drawing.customInput')}</option>
    </select>
  );
}
